import type { CustomNoRowsOverlayProps } from "ag-grid-react";

import { Box, Icon } from "@mui/material";
import { useTranslation } from "react-i18next";

import { ClearButton } from "~/components/Buttons";
import { type GridEmptyStateParams, resolveGridEmptyState } from "~/components/grid/gridOverlayState";
import RetryableErrorState from "~/components/RetryableErrorState";
import { BodyText, BodyTextSmall } from "~/components/Typographies";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import { updateGridSearchText } from "~/store/layoutSlice";

type GridEmptyStateProps = CustomNoRowsOverlayProps & GridEmptyStateParams;

const GridEmptyState = (props: GridEmptyStateProps) => {
	const { t } = useTranslation("common");
	const dispatch = useAppDispatch();
	const quickFilterEnabled = props.quickFilterEmptyState === true;
	const searchTerm = useAppSelector((state) => (quickFilterEnabled ? state.layout.gridSearchText.trim() : ""));

	if (props.isError === true) {
		return (
			<Box
				sx={
					props.shouldCoverRows === true
						? {
								alignItems: "center",
								bgcolor: "background.paper",
								bottom: 0,
								display: "flex",
								justifyContent: "center",
								left: 0,
								pointerEvents: "auto",
								position: "absolute",
								right: 0,
								top: "var(--ag-header-height)"
							}
						: { pointerEvents: "auto" }
				}
			>
				<RetryableErrorState message={props.errorMessage} onRetry={props.onRetry} />
			</Box>
		);
	}

	const content = resolveGridEmptyState(props, props.api.isAnyFilterPresent(), searchTerm);

	const action = searchTerm ? (
		<ClearButton onClick={() => dispatch(updateGridSearchText(""))} size="small">
			{t("grid.emptyState.clearSearch")}
		</ClearButton>
	) : (
		props.action
	);

	return (
		<Box
			sx={{
				alignItems: "center",
				color: "text.secondary",
				display: "flex",
				flexDirection: "column",
				// AG Grid's overlay wrapper sets pointer-events: none on itself and its descendants, which is inherited here and blocks real clicks on the action button.
				p: 4,
				pointerEvents: "auto",
				textAlign: "center"
			}}
		>
			<Icon sx={{ color: "text.secondary", fontSize: 48, mb: 1 }}>{content.icon}</Icon>
			<BodyText sx={{ color: "text.secondary" }}>{content.title}</BodyText>
			{content.message && <BodyTextSmall sx={{ color: "text.disabled", mt: 0.5 }}>{content.message}</BodyTextSmall>}
			{action && <Box sx={{ mt: 2 }}>{action}</Box>}
		</Box>
	);
};

export default GridEmptyState;
